import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import api from '../utils/api';
import { getUserInfo, isUserLoggedIn, logoutUser } from '../utils/auth';
import { showSuccess, showError } from '../utils/toast';
import { Heart, User, Briefcase, FileText, LogOut, Edit3, Plus, Camera, Info, MapPin, Calendar, CheckCircle } from 'lucide-react';
import Pagination from '../components/Pagination';
import ImageUpload from '../components/ImageUpload';
import { stripHtmlTags } from '../utils/htmlUtils';

const UserProfile = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    city: '',
    tags: [],
    address: '',
    phone: '',
    email: '',
    website: '',
    description: ''
  });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [businessImages, setBusinessImages] = useState([]);
  const [deletedBusinessImageIds, setDeletedBusinessImageIds] = useState([]);
  const [businessFormLoading, setBusinessFormLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsPagination, setReportsPagination] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const navigate = useNavigate();
  const loggedIn = isUserLoggedIn();

  // Active Tab State
  const [activeTab, setActiveTab] = useState('businesses');

  useEffect(() => {
    if (!loggedIn) {
      navigate('/login', { state: { from: { pathname: '/profile' } } });
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch full user profile from backend
        const userResponse = await api.get('/user-auth/profile');
        const fullUserInfo = userResponse.data;
        setUserInfo(fullUserInfo);

        // Also update localStorage with full info
        const storedInfo = {
          id: fullUserInfo._id,
          name: fullUserInfo.name,
          email: fullUserInfo.email,
          phone: fullUserInfo.phone,
          avatar: fullUserInfo.avatar
        };
        localStorage.setItem('userInfo', JSON.stringify(storedInfo));

        // Fetch user's businesses with pagination
        const businessesResponse = await api.get(`/businesses/my-businesses?page=${currentPage}`);
        if (businessesResponse.data && businessesResponse.data.businesses) {
          setBusinesses(businessesResponse.data.businesses);
          setPagination(businessesResponse.data.pagination);
        } else if (Array.isArray(businessesResponse.data)) {
          // Fallback for non-paginated response
          setBusinesses(businessesResponse.data);
          setPagination(null);
        } else {
          setBusinesses([]);
          setPagination(null);
        }

        // Fetch categories, tags, cities, reports, and favourites
        const [catsRes, tagsRes, citiesRes, reportsRes, favRes] = await Promise.all([
          api.get('/categories'),
          api.get('/tags'),
          api.get('/cities'),
          api.get('/reports/my-reports?page=1&limit=20').catch(() => ({ data: { reports: [], pagination: null } })),
          api.get('/favourites').catch(() => ({ data: { favourites: [] } }))
        ]);
        setCategories(catsRes.data);
        setTags(tagsRes.data);
        setCities(citiesRes.data);
        if (reportsRes?.data?.reports) {
          setReports(reportsRes.data.reports);
          setReportsPagination(reportsRes.data.pagination || null);
        } else {
          setReports([]);
          setReportsPagination(null);
        }
        setFavourites(favRes?.data?.favourites || []);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        if (error.response?.status === 401) {
          logoutUser();
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loggedIn, navigate, currentPage]);

  const handleLogout = () => {
    logoutUser();
    showSuccess('Logged out successfully!');
    navigate('/');
  };

  const handleEditClick = () => {
    setEditFormData({
      name: userInfo.name || '',
      email: userInfo.email || '',
      phone: userInfo.phone || '',
      password: '',
      confirmPassword: ''
    });
    setShowEditForm(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (editFormData.password && editFormData.password !== editFormData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    setEditLoading(true);
    try {
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone || null
      };

      // Only include password if it's being changed
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      const response = await api.put('/user-auth/profile', updateData);

      // Update local state and localStorage
      const updatedUser = response.data.user;
      setUserInfo(updatedUser);
      localStorage.setItem('userInfo', JSON.stringify({
        id: updatedUser._id,
        name: updatedUser.name,
        email: updatedUser.email,
        phone: updatedUser.phone,
        avatar: updatedUser.avatar
      }));

      showSuccess('Profile updated successfully!');
      setShowEditForm(false);
      setEditFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating profile');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditBusiness = async (business) => {
    try {
      // Fetch subcategories for the selected category
      let subcats = [];
      if (business.category?._id) {
        const subcatsRes = await api.get(`/subcategories?categoryId=${business.category._id}`);
        subcats = subcatsRes.data;
      }

      setEditingBusiness(business);
      setBusinessFormData({
        name: business.name || '',
        category: business.category?._id || '',
        subcategory: business.subcategory?._id || '',
        city: business.city?._id || '',
        tags: business.tags?.map(t => t._id) || [],
        address: business.address || '',
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        description: business.description || ''
      });
      setSelectedTags(business.tags?.map(t => t._id) || []);
      setSubcategories(subcats);

      // Populate already attached images so they are visible and editable
      if (business.images && Array.isArray(business.images) && business.images.length > 0) {
        const existingImgs = business.images.map((img, idx) => ({
          url: img.url,
          isPrimary: Boolean(img.isPrimary) || (idx === 0 && !business.images.some(i => i.isPrimary)),
          file: null,
          existing: true,
          id: img._id,
          _id: img._id
        }));
        setBusinessImages(existingImgs);
      } else {
        setBusinessImages([]);
      }
      setDeletedBusinessImageIds([]);
    } catch (error) {
      showError('Error loading business data');
    }
  };

  const handleBusinessImagesChange = (updatedImages) => {
    setBusinessImages(updatedImages);
  };

  const handleBusinessImageDelete = (deletedImg) => {
    if (deletedImg?.existing && (deletedImg.id || deletedImg._id)) {
      setDeletedBusinessImageIds(prev => [...prev, deletedImg.id || deletedImg._id]);
    }
  };

  const handleBusinessFormChange = (e) => {
    setBusinessFormData({
      ...businessFormData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    if (businessFormData.category) {
      const fetchSubcategories = async () => {
        try {
          const response = await api.get(`/subcategories?categoryId=${businessFormData.category}`);
          setSubcategories(response.data);
        } catch (error) {
          console.error('Error fetching subcategories:', error);
        }
      };
      fetchSubcategories();
    } else {
      setSubcategories([]);
    }
  }, [businessFormData.category]);

  const handleTagToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
      setBusinessFormData({
        ...businessFormData,
        tags: businessFormData.tags.filter(id => id !== tagId)
      });
    } else {
      setSelectedTags([...selectedTags, tagId]);
      setBusinessFormData({
        ...businessFormData,
        tags: [...businessFormData.tags, tagId]
      });
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setBusinessFormLoading(true);

    try {
      const submitData = {
        ...businessFormData,
        tags: selectedTags
      };

      await api.put(`/businesses/my-businesses/${editingBusiness._id}`, submitData);

      // 1. Delete removed existing images
      if (deletedBusinessImageIds.length > 0) {
        for (const imgId of deletedBusinessImageIds) {
          try {
            await api.delete(`/businesses/${editingBusiness._id}/images/${imgId}`);
          } catch (delErr) {
            console.error('Error deleting image:', delErr);
          }
        }
      }

      // 2. Upload any new image files
      const newImages = businessImages.filter(img => img.file);
      let uploadedImages = [];
      if (newImages.length > 0) {
        const imageFormData = new FormData();
        newImages.forEach((img) => {
          imageFormData.append('images', img.file);
        });

        const primaryNewIndex = newImages.findIndex(img => img.isPrimary);
        if (primaryNewIndex !== -1) {
          imageFormData.append('primaryIndex', primaryNewIndex);
        }

        try {
          const uploadRes = await api.post(`/businesses/${editingBusiness._id}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploadedImages = uploadRes.data?.images || [];
        } catch (imgError) {
          console.error('Error uploading images:', imgError);
          showError('Business updated but some images failed to upload');
        }
      }

      // 3. Update primary image
      const primaryImg = businessImages.find(img => img.isPrimary);
      if (primaryImg) {
        if (primaryImg.existing && (primaryImg.id || primaryImg._id)) {
          try {
            await api.patch(`/businesses/${editingBusiness._id}/images/${primaryImg.id || primaryImg._id}/primary`);
          } catch (primErr) {
            console.error('Error updating primary image:', primErr);
          }
        } else if (!primaryImg.existing && uploadedImages.length > 0) {
          const primaryNewIndex = newImages.findIndex(img => img.isPrimary);
          const targetUploaded = (primaryNewIndex !== -1 && uploadedImages[primaryNewIndex])
            ? uploadedImages[primaryNewIndex]
            : uploadedImages[0];
          if (targetUploaded && (targetUploaded._id || targetUploaded.id)) {
            try {
              await api.patch(`/businesses/${editingBusiness._id}/images/${targetUploaded._id || targetUploaded.id}/primary`);
            } catch (primErr) {
              console.error('Error setting primary image for new upload:', primErr);
            }
          }
        }
      }

      showSuccess('Business updated successfully! It will be reviewed by admin again.');

      // Refresh businesses list
      const response = await api.get(`/businesses/my-businesses?page=${currentPage}`);
      if (response.data && response.data.businesses) {
        setBusinesses(response.data.businesses);
      } else if (Array.isArray(response.data)) {
        setBusinesses(response.data);
      }

      setEditingBusiness(null);
      setBusinessFormData({
        name: '',
        category: '',
        subcategory: '',
        city: '',
        tags: [],
        address: '',
        phone: '',
        email: '',
        website: '',
        description: ''
      });
      setSelectedTags([]);
      setBusinessImages([]);
      setDeletedBusinessImageIds([]);
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating business');
    } finally {
      setBusinessFormLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/20';
      case 'pending':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 ring-1 ring-inset ring-yellow-600/20';
      case 'rejected':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/20';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-600/20';
    }
  };

  const handleRemoveFavourite = async (businessId) => {
    try {
      await api.delete(`/favourites/${businessId}`);
      setFavourites((prev) => prev.filter((b) => b._id !== businessId));
      showSuccess('Removed from favourites');
    } catch (err) {
      showError(err.response?.data?.message || 'Could not remove from favourites');
    }
  };

  if (loading) {
    return <PageLoader message="Loading your profile..." fullScreen />;
  }

  if (!userInfo) {
    return null;
  }

  // Helper to render the correct section based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'businesses':
        return (
          <div className="animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-500" />
                  My Businesses
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your listed businesses</p>
              </div>
              <Link to="/submit-business" className="md:hidden btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add
              </Link>
            </div>

            {businesses.length > 0 ? (
              <div className="space-y-4">
                {businesses.map((business) => (
                  <div key={business._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 text-2xl font-bold text-gray-400 dark:text-gray-500">
                          {business.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">{business.name}</h3>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span>{business.category?.name}</span>
                            <span>•</span>
                            <span>{business.city?.name}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(business.status)}`}>
                              {business.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                              {business.status}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(business.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => handleEditBusiness(business)}
                        className="p-2 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                  <Briefcase className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No businesses yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by listing your first business</p>
                <div className="mt-6">
                  <Link
                    to="/submit-business"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Business
                  </Link>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={(page) => {
                    setSearchParams({ page: page.toString() });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>
            )}
          </div>
        );

      case 'favourites':
        return (
          <div className="animate-fadeIn">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                My Favourites
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Saved businesses and listings</p>
            </div>

            {favourites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                {favourites.map((b) => (
                  <div key={b._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <Link to={b.category?.slug && b.slug ? `/${b.category.slug}/${b.slug}` : '#'} className="block">
                        <h3 className="font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate transition-colors">
                          {b.name || 'Unknown Business'}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
                        {b.category?.name && <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-[10px]">{b.category.name}</span>}
                        {b.city?.name && <span>{b.city.name}</span>}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {b.phone || b.address || 'No contact info'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFavourite(b._id)}
                      className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-full self-start transition-colors"
                      title="Remove from favourites"
                    >
                      <Heart className="w-4 h-4 fill-current" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
                  <Heart className="h-6 w-6 text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No favourites yet</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Save businesses you like to find them easily later</p>
              </div>
            )}
          </div>
        );

      case 'reports':
        return (
          <div className="animate-fadeIn">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-500" />
                Report History
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Status of your submitted reports</p>
            </div>

            {reports.length > 0 ? (
              <div className="space-y-4">
                {reports.map((report) => (
                  <div key={report._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{report.business?.name || 'Deleted Business'}</h3>
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${report.status === 'resolved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                          report.status === 'dismissed' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-300' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                        {report.status}
                      </span>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-300 mb-3 italic">
                      "{report.reason}"
                      {report.comment && <div className="mt-1 text-xs text-gray-500 not-italic border-t border-gray-200 dark:border-gray-700 pt-1">{report.comment}</div>}
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(report.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                  <FileText className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No reports found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">You haven't submitted any reports yet</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO title="My Profile" description="Your Digital Fatehabad profile, businesses, and favourites." noindex />

      <div className="min-h-screen bg-gray-50/50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

            {/* Left Sidebar: Profile Card */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden sticky top-24">
                {/* Header Background */}
                <div className="h-24 bg-gradient-to-r from-primary-500 to-primary-600 relative">
                  <div className="absolute inset-0 bg-black/10"></div>
                </div>

                <div className="px-6 pb-6 text-center relative">
                  {/* Avatar */}
                  <div className="-mt-12 mb-4 relative inline-block">
                    <div className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-900 shadow-md bg-white dark:bg-gray-800 overflow-hidden flex items-center justify-center text-3xl font-bold text-gray-300">
                      {userInfo.avatar ? (
                        <img src={userInfo.avatar} alt={userInfo.name} className="w-full h-full object-cover" />
                      ) : (
                        userInfo.name.charAt(0).toUpperCase()
                      )}
                    </div>
                  </div>

                  {/* User Info */}
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{userInfo.name}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{userInfo.email}</p>

                  {/* Stats Row */}
                  <div className="flex justify-center divide-x divide-gray-200 dark:divide-gray-700 border-t border-b border-gray-100 dark:border-gray-800 py-4 mb-6">
                    <div className="px-4 text-center">
                      <div className="font-bold text-gray-900 dark:text-white">{businesses.length}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Listings</div>
                    </div>
                    <div className="px-4 text-center">
                      <div className="font-bold text-gray-900 dark:text-white">{favourites.length}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Saved</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <button
                      onClick={handleEditClick}
                      className="w-full py-2.5 px-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Profile
                    </button>

                    <Link
                      to="/submit-business"
                      className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
                    >
                      <Plus className="w-4 h-4" />
                      Add Business
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="w-full py-2.5 px-4 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Log Out
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {/* Mobile Tabs (Scrollable) */}
              <div className="flex md:hidden overflow-x-auto pb-4 gap-2 mb-4 scrollbar-hide">
                {[
                  { id: 'businesses', label: 'Businesses', icon: Briefcase },
                  { id: 'favourites', label: 'Favourites', icon: Heart },
                  { id: 'reports', label: 'Reports', icon: Briefcase } // Using briefcase as placeholder or file-text
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                      }`}
                  >
                    {/* Icon logic simplified for map, can use tab.icon */}
                    <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-white' : ''}`} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Desktop Tabs */}
              <div className="hidden md:flex bg-white dark:bg-gray-900 rounded-xl p-1 mb-6 shadow-sm border border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setActiveTab('businesses')}
                  className={`flex-1 py-3 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-all ${activeTab === 'businesses'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  <Briefcase className="w-4 h-4" />
                  My Businesses
                </button>
                <button
                  onClick={() => setActiveTab('favourites')}
                  className={`flex-1 py-3 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-all ${activeTab === 'favourites'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  <Heart className="w-4 h-4" />
                  Favourites
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`flex-1 py-3 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-all ${activeTab === 'reports'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  <FileText className="w-4 h-4" />
                  Reports
                </button>
              </div>

              {/* Dynamic Content */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-6 min-h-[400px]">
                {loading ? (
                  <div className="flex justify-center items-center h-full py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  renderTabContent()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal (Overlay) */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-scaleIn">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit Profile</h3>
              <button onClick={() => setShowEditForm(false)} className="text-gray-400 hover:text-gray-600">
                <LogOut className="w-5 h-5 rotate-45" /> {/* Close icon using LogOut rotated or just X */}
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white"
                />
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-sm text-primary-600 font-medium hover:underline flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" /> Change Password
                </button>
              </div>

              {showPassword && (
                <div className="space-y-4 pt-2 animate-fadeIn">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">New Password</label>
                    <input
                      type="password"
                      value={editFormData.password}
                      onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white"
                      placeholder="Leave blank to keep current"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      value={editFormData.confirmPassword}
                      onChange={(e) => setEditFormData({ ...editFormData, confirmPassword: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all dark:text-white"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 py-3 px-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Business Modal - Kept relatively same logic but styled better if needed */}
      {editingBusiness && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Business</h2>
              <button
                onClick={() => {
                  setEditingBusiness(null);
                  setBusinessFormData({ name: '', category: '', subcategory: '', city: '', tags: [], address: '', phone: '', email: '', website: '', description: '' });
                  setSelectedTags([]);
                  setBusinessImages([]);
                  setDeletedBusinessImageIds([]);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <LogOut className="w-6 h-6 rotate-45" />
              </button>
            </div>

            <form onSubmit={handleBusinessSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Business Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={businessFormData.name}
                    onChange={handleBusinessFormChange}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={businessFormData.phone}
                    onChange={handleBusinessFormChange}
                    className="input-field"
                  />
                </div>
                {/* ... Rest of fields simplified for length, using standard input-field classes found in index.css typically ... */}
                {/* Re-use existing form logic but ensure classes match new theme */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Category *</label>
                  <select
                    name="category"
                    value={businessFormData.category}
                    onChange={handleBusinessFormChange}
                    required
                    className="input-field"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">City</label>
                  <select
                    name="city"
                    value={businessFormData.city}
                    onChange={handleBusinessFormChange}
                    className="input-field"
                  >
                    <option value="">Select a city</option>
                    {cities.map((city) => (
                      <option key={city._id} value={city._id}>{city.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  name="description"
                  value={businessFormData.description}
                  onChange={handleBusinessFormChange}
                  rows="4"
                  className="input-field"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Business Images (Already Attached & New)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Manage your business photos (up to 10). Already attached images are shown below with "Attached" badge.
                </p>
                <ImageUpload
                  images={businessImages}
                  onImagesChange={handleBusinessImagesChange}
                  onImageDelete={handleBusinessImageDelete}
                  maxImages={10}
                />
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    setEditingBusiness(null);
                    setBusinessFormData({ name: '', category: '', subcategory: '', city: '', tags: [], address: '', phone: '', email: '', website: '', description: '' });
                    setSelectedTags([]);
                    setBusinessImages([]);
                    setDeletedBusinessImageIds([]);
                  }}
                  className="px-6 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={businessFormLoading}
                  className="px-6 py-2.5 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 shadow-lg shadow-primary-500/20 disabled:opacity-50"
                >
                  {businessFormLoading ? 'Updating...' : 'Update Business'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserProfile;
